/**
 * Pricing Rules Calculator Service
 * Evaluates pricing rules against occupancy data and provides detailed breakdowns
 * Used by Calendar and PricingRulesManager to show rule status
 */

import { supabase } from '../../../services/supabaseClient';
import {
  PricingRule,
  ComparisonOperator,
  OccupancyScope,
} from '../../../types/revenue';
import {
  isExclusiveChannelOverride,
  selectPriceRules,
} from '../../../services/channelOverrideResolver';
import {
  describeBookingWindow,
  isWithinBookingWindow,
  resolveBookingWindowBounds,
} from '../../../services/bookingWindowResolver';
import { format, differenceInDays, addDays, subDays } from 'date-fns';

/**
 * Evaluate a condition with a configurable comparison operator
 */
export function evaluateCondition(
  value: number,
  operator: ComparisonOperator | undefined,
  threshold: number
): boolean {
  switch (operator) {
    case 'gt':
      return value > threshold;
    case 'gte':
      return value >= threshold;
    case 'lt':
      return value < threshold;
    case 'lte':
      return value <= threshold;
    case 'eq':
      return value === threshold;
    default:
      // Default to >= for backward compatibility
      return value >= threshold;
  }
}

/**
 * Get the display symbol for an operator
 */
export function getOperatorSymbol(
  operator: ComparisonOperator | undefined
): string {
  switch (operator) {
    case 'gt':
      return '>';
    case 'gte':
      return '≥';
    case 'lt':
      return '<';
    case 'lte':
      return '≤';
    case 'eq':
      return '=';
    default:
      return '≥';
  }
}

export interface RuleEvaluationResult {
  ruleId: string;
  ruleName: string;
  ruleType: string;
  isActive: boolean;
  threshold: number;
  currentValue: number;
  adjustment: { type: 'percentage' | 'amount'; value: number };
  adjustmentAmount: number;
  reason: string;
}

export interface DayPricingBreakdown {
  date: string;
  roomTypeCode: string;
  roomTypeName: string;
  occupancy: number;
  daysUntilArrival: number;
  basePrice: number;
  evaluatedRules: RuleEvaluationResult[];
  activeRules: RuleEvaluationResult[];
  finalPrice: number;
  totalAdjustment: number;
  totalAdjustmentPercent: number;
}

export interface OccupancyStatus {
  date: string;
  overall: number;
  totalRooms: number;
  bookedRooms: number;
  byRoomType: Array<{
    roomTypeCode: string;
    roomTypeName: string;
    occupancy: number;
    totalRooms: number;
    bookedRooms: number;
    availableRooms: number;
  }>;
}

export interface RuleActiveDaysCount {
  ruleId: string;
  activeDays: number;
  totalDays: number;
  nextActiveDate: string | null;
}

export interface ActiveRuleInfo {
  count: number;
  totalAdjustmentPercent: number;
  rules: Array<{
    id: string;
    name: string;
    ruleType: string;
    adjustmentPercent: number;
    roomTypes?: string[];
  }>;
}

class PricingRulesCalculatorService {
  /**
   * Get occupancy data for a specific date
   */
  async getOccupancyForDate(
    propertyId: string,
    date: Date
  ): Promise<OccupancyStatus> {
    const dateStr = format(date, 'yyyy-MM-dd');

    const { data, error } = await supabase
      .from('calendar_data')
      .select(
        'room_type_code, room_type_name, total_rooms, booked_rooms, available_rooms, occupancy_percentage'
      )
      .eq('property_id', propertyId)
      .eq('date', dateStr);

    if (error) {
      console.error(
        '[PricingRulesCalculator] Error fetching occupancy:',
        error
      );
      return {
        date: dateStr,
        overall: 0,
        totalRooms: 0,
        bookedRooms: 0,
        byRoomType: [],
      };
    }

    const byRoomType = (data || []).map((row) => ({
      roomTypeCode: row.room_type_code,
      roomTypeName: row.room_type_name || row.room_type_code,
      occupancy: row.occupancy_percentage || 0,
      totalRooms: row.total_rooms || 0,
      bookedRooms: row.booked_rooms || 0,
      availableRooms: row.available_rooms || 0,
    }));

    const totalRooms = byRoomType.reduce((sum, rt) => sum + rt.totalRooms, 0);
    const bookedRooms = byRoomType.reduce((sum, rt) => sum + rt.bookedRooms, 0);
    const overall =
      totalRooms > 0 ? Math.round((bookedRooms / totalRooms) * 100) : 0;

    return {
      date: dateStr,
      overall,
      totalRooms,
      bookedRooms,
      byRoomType,
    };
  }

  /**
   * Get occupancy data for a date range
   */
  async getOccupancyForRange(
    propertyId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Map<string, OccupancyStatus>> {
    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');

    const { data, error } = await supabase
      .from('calendar_data')
      .select(
        'date, room_type_code, room_type_name, total_rooms, booked_rooms, available_rooms, occupancy_percentage'
      )
      .eq('property_id', propertyId)
      .gte('date', startStr)
      .lte('date', endStr);

    if (error) {
      console.error(
        '[PricingRulesCalculator] Error fetching occupancy range:',
        error
      );
      return new Map();
    }

    // Group by date
    const byDate = new Map<string, OccupancyStatus>();
    const grouped = new Map<string, typeof data>();

    for (const row of data || []) {
      const dateData = grouped.get(row.date) || [];
      dateData.push(row);
      grouped.set(row.date, dateData);
    }

    for (const [dateStr, rows] of grouped) {
      const byRoomType = rows.map((row) => ({
        roomTypeCode: row.room_type_code,
        roomTypeName: row.room_type_name || row.room_type_code,
        occupancy: row.occupancy_percentage || 0,
        totalRooms: row.total_rooms || 0,
        bookedRooms: row.booked_rooms || 0,
        availableRooms: row.available_rooms || 0,
      }));

      const totalRooms = byRoomType.reduce((sum, rt) => sum + rt.totalRooms, 0);
      const bookedRooms = byRoomType.reduce(
        (sum, rt) => sum + rt.bookedRooms,
        0
      );
      const overall =
        totalRooms > 0 ? Math.round((bookedRooms / totalRooms) * 100) : 0;

      byDate.set(dateStr, {
        date: dateStr,
        overall,
        totalRooms,
        bookedRooms,
        byRoomType,
      });
    }

    return byDate;
  }

  /**
   * Evaluate all rules for a specific date and room type
   */
  async evaluateRulesForDay(
    propertyId: string,
    roomTypeCode: string,
    date: Date,
    occupancy: number,
    basePrice: number
  ): Promise<DayPricingBreakdown> {
    const dateStr = format(date, 'yyyy-MM-dd');
    const daysUntilArrival = differenceInDays(date, new Date());

    // Fetch overall property occupancy for this date
    const occupancyStatus = await this.getOccupancyForDate(propertyId, date);
    const overallOccupancy = occupancyStatus.overall;

    // Get all active rules for this property
    const { data: rules, error } = await supabase
      .from('pricing_rules')
      .select('*')
      .eq('property_id', propertyId)
      .eq('active', true)
      .order('priority');

    if (error) {
      console.error('[PricingRulesCalculator] Error fetching rules:', error);
      return {
        date: dateStr,
        roomTypeCode,
        roomTypeName: roomTypeCode,
        occupancy,
        daysUntilArrival,
        basePrice,
        evaluatedRules: [],
        activeRules: [],
        finalPrice: basePrice,
        totalAdjustment: 0,
        totalAdjustmentPercent: 0,
      };
    }

    let evaluatedRules: RuleEvaluationResult[] = [];

    for (const rule of rules || []) {
      const evaluation = this.evaluateSingleRule(
        rule as PricingRule,
        roomTypeCode,
        date,
        occupancy,
        daysUntilArrival,
        basePrice,
        overallOccupancy
      );
      evaluatedRules.push(evaluation);
    }

    // Exclusive channel_override gate — MUST agree with selectPriceRules in
    // the edge function and the bridge: when an exclusive override is active
    // for this date/room, only the winner keeps its adjustment; every other
    // active price rule is shown as silenced.
    const activeIds = new Set(
      evaluatedRules.filter((e) => e.isActive).map((e) => e.ruleId)
    );
    const activeExclusives = ((rules || []) as PricingRule[]).filter(
      (r) => isExclusiveChannelOverride(r) && activeIds.has(r.id)
    );
    if (activeExclusives.length > 0) {
      const winner = selectPriceRules(activeExclusives)[0];
      evaluatedRules = evaluatedRules.map((e) =>
        e.isActive && e.ruleId !== winner?.id
          ? {
              ...e,
              isActive: false,
              adjustmentAmount: 0,
              reason: 'Silenced by exclusive channel override',
            }
          : e
      );
    }

    const totalAdjustment = evaluatedRules.reduce(
      (sum, e) => (e.isActive ? sum + e.adjustmentAmount : sum),
      0
    );

    const activeRules = evaluatedRules.filter((r) => r.isActive);
    const finalPrice = Math.max(0, basePrice + totalAdjustment);
    const totalAdjustmentPercent =
      basePrice > 0 ? (totalAdjustment / basePrice) * 100 : 0;

    return {
      date: dateStr,
      roomTypeCode,
      roomTypeName: roomTypeCode,
      occupancy,
      daysUntilArrival,
      basePrice,
      evaluatedRules,
      activeRules,
      finalPrice,
      totalAdjustment,
      totalAdjustmentPercent,
    };
  }

  /**
   * Evaluate a single rule against the current context
   */
  private evaluateSingleRule(
    rule: PricingRule,
    roomTypeCode: string,
    targetDate: Date,
    roomTypeOccupancy: number,
    daysUntilArrival: number,
    basePrice: number,
    overallOccupancy: number
  ): RuleEvaluationResult {
    const dateStr = format(targetDate, 'yyyy-MM-dd');

    // Check scope - if room_type scope, verify room type is in list
    if (rule.scope === 'room_type') {
      if (!rule.room_type_ids || !rule.room_type_ids.includes(roomTypeCode)) {
        return this.createInactiveResult(
          rule,
          0,
          roomTypeOccupancy,
          `Room type ${roomTypeCode} not in rule scope`
        );
      }
    }

    // Check date range validity (top-level fields or conditions.date_range)
    const condDateRange = rule.conditions?.['date_range'] as
      | { start_date?: string; end_date?: string }
      | undefined;
    const validFrom = rule.valid_from || condDateRange?.start_date;
    const validUntil = rule.valid_until || condDateRange?.end_date;
    if (validFrom && dateStr < validFrom) {
      return this.createInactiveResult(
        rule,
        0,
        roomTypeOccupancy,
        `Date before valid_from (${validFrom})`
      );
    }
    if (validUntil && dateStr > validUntil) {
      return this.createInactiveResult(
        rule,
        0,
        roomTypeOccupancy,
        `Date after valid_until (${validUntil})`
      );
    }

    // Resolve occupancy based on rule's occupancy_scope
    const occupancy = this.resolveOccupancy(
      rule,
      roomTypeOccupancy,
      overallOccupancy
    );

    // Evaluate based on rule type
    switch (rule.rule_type) {
      case 'high_occupancy':
        return this.evaluateHighOccupancyRule(
          rule,
          occupancy,
          basePrice,
          daysUntilArrival
        );
      case 'low_occupancy':
        return this.evaluateLowOccupancyRule(
          rule,
          occupancy,
          basePrice,
          daysUntilArrival
        );
      case 'booking_window':
        return this.evaluateBookingWindowRule(
          rule,
          occupancy,
          daysUntilArrival,
          basePrice
        );
      case 'min_los':
        return this.createInactiveResult(
          rule,
          0,
          occupancy,
          'MinLOS rules affect restrictions, not price'
        );
      case 'gap_filling':
        return this.createInactiveResult(
          rule,
          0,
          occupancy,
          'Gap filling requires reservation context'
        );
      case 'channel_override':
        return this.evaluateChannelOverrideRule(rule, occupancy, basePrice);
      default:
        return this.createInactiveResult(
          rule,
          0,
          occupancy,
          `Unknown rule type: ${rule.rule_type}`
        );
    }
  }

  /**
   * Channel override: unconditional ±%/±€ adjustment for its date period.
   * Scope and date range were already checked by evaluateSingleRule; the
   * exclusive gate (silencing other rules) runs in evaluateRulesForDay so a
   * silenced rule still shows WHY it was silenced.
   */
  private evaluateChannelOverrideRule(
    rule: PricingRule,
    occupancy: number,
    basePrice: number
  ): RuleEvaluationResult {
    const adjustmentValue =
      (rule.actions?.price_adjustment?.value as number) || 0;
    const adjustmentType =
      (rule.actions?.price_adjustment?.type as 'percentage' | 'amount') ||
      'percentage';
    const exclusive = rule.conditions?.['exclusive'] === true;
    const adjustmentAmount =
      adjustmentType === 'percentage'
        ? (basePrice * adjustmentValue) / 100
        : adjustmentValue;

    return {
      ruleId: rule.id,
      ruleName: rule.rule_name || rule.name,
      ruleType: rule.rule_type,
      isActive: true,
      threshold: 0,
      currentValue: occupancy,
      adjustment: { type: adjustmentType, value: adjustmentValue },
      adjustmentAmount,
      reason: `Channel override${exclusive ? ' (exclusive)' : ''} active for this period`,
    };
  }

  private evaluateHighOccupancyRule(
    rule: PricingRule,
    occupancy: number,
    basePrice: number,
    daysUntilArrival: number
  ): RuleEvaluationResult {
    // Defaults mirror the pricing-engine edge function (threshold 0 = always).
    const threshold = (rule.conditions?.occupancy_threshold as number) ?? 0;
    const operator = (rule.conditions?.operator as ComparisonOperator) ?? 'gte';
    const adjustmentValue =
      (rule.actions?.price_adjustment?.value as number) || 0;
    const adjustmentType =
      (rule.actions?.price_adjustment?.type as 'percentage' | 'amount') ||
      'percentage';

    // Lead-time gate, same as the engine: only within booking_window_days.
    const windowDays = rule.conditions?.booking_window_days as
      | number
      | undefined;
    const withinBookingWindow =
      windowDays == null || daysUntilArrival <= windowDays;

    const isActive =
      withinBookingWindow && evaluateCondition(occupancy, operator, threshold);
    const adjustmentAmount = isActive
      ? adjustmentType === 'percentage'
        ? (basePrice * adjustmentValue) / 100
        : adjustmentValue
      : 0;

    const symbol = getOperatorSymbol(operator);
    const scopeLabel = this.getOccupancyScopeLabel(rule);
    return {
      ruleId: rule.id,
      ruleName: rule.rule_name || rule.name,
      ruleType: rule.rule_type,
      isActive,
      threshold,
      currentValue: occupancy,
      adjustment: { type: adjustmentType, value: adjustmentValue },
      adjustmentAmount,
      reason: isActive
        ? `${scopeLabel} occupancy ${occupancy.toFixed(0)}% ${symbol} ${threshold}%`
        : `${scopeLabel} occupancy ${occupancy.toFixed(0)}% not ${symbol} ${threshold}% (threshold not met)`,
    };
  }

  private evaluateLowOccupancyRule(
    rule: PricingRule,
    occupancy: number,
    basePrice: number,
    daysUntilArrival: number
  ): RuleEvaluationResult {
    // Defaults mirror the pricing-engine edge function ('lt' / 100).
    const threshold = (rule.conditions?.occupancy_threshold as number) ?? 100;
    const operator = (rule.conditions?.operator as ComparisonOperator) ?? 'lt';
    const adjustmentValue =
      (rule.actions?.price_adjustment?.value as number) || 0;
    const adjustmentType =
      (rule.actions?.price_adjustment?.type as 'percentage' | 'amount') ||
      'percentage';

    // Lead-time gate, same as the engine: only within booking_window_days.
    const windowDays = rule.conditions?.booking_window_days as
      | number
      | undefined;
    const withinBookingWindow =
      windowDays == null || daysUntilArrival <= windowDays;

    const isActive =
      withinBookingWindow && evaluateCondition(occupancy, operator, threshold);
    const adjustmentAmount = isActive
      ? adjustmentType === 'percentage'
        ? (basePrice * adjustmentValue) / 100
        : adjustmentValue
      : 0;

    const symbol = getOperatorSymbol(operator);
    const scopeLabel = this.getOccupancyScopeLabel(rule);
    return {
      ruleId: rule.id,
      ruleName: rule.rule_name || rule.name,
      ruleType: rule.rule_type,
      isActive,
      threshold,
      currentValue: occupancy,
      adjustment: { type: adjustmentType, value: adjustmentValue },
      adjustmentAmount,
      reason: isActive
        ? `${scopeLabel} occupancy ${occupancy.toFixed(0)}% ${symbol} ${threshold}%`
        : `${scopeLabel} occupancy ${occupancy.toFixed(0)}% not ${symbol} ${threshold}% (threshold not met)`,
    };
  }

  private evaluateBookingWindowRule(
    rule: PricingRule,
    occupancy: number,
    daysUntilArrival: number,
    basePrice: number
  ): RuleEvaluationResult {
    const windowBounds = resolveBookingWindowBounds(rule, 14);
    const windowDays = windowBounds.max ?? 14;
    const occupancyThreshold =
      (rule.conditions?.occupancy_threshold as number) ?? 60;
    const occupancyOperator =
      (rule.conditions?.occupancy_operator as ComparisonOperator) ?? 'gte';
    const adjustmentValue =
      (rule.actions?.price_adjustment?.value as number) || 0;
    const adjustmentType =
      (rule.actions?.price_adjustment?.type as 'percentage' | 'amount') ||
      'percentage';

    const withinWindow = isWithinBookingWindow(daysUntilArrival, windowBounds);
    const occupancyMet = evaluateCondition(
      occupancy,
      occupancyOperator,
      occupancyThreshold
    );
    const isActive = withinWindow && occupancyMet;

    const adjustmentAmount = isActive
      ? adjustmentType === 'percentage'
        ? (basePrice * adjustmentValue) / 100
        : adjustmentValue
      : 0;

    const operatorSymbol = getOperatorSymbol(occupancyOperator);
    const scopeLabel = this.getOccupancyScopeLabel(rule);
    let reason: string;
    if (!withinWindow) {
      reason = `${daysUntilArrival} days out, outside ${describeBookingWindow(windowBounds)} window`;
    } else if (!occupancyMet) {
      reason = `${scopeLabel} occupancy ${occupancy.toFixed(0)}% not ${operatorSymbol} ${occupancyThreshold}% threshold`;
    } else {
      reason = `${describeBookingWindow(windowBounds)} + ${scopeLabel.toLowerCase()} occupancy ${occupancy.toFixed(0)}% ${operatorSymbol} ${occupancyThreshold}%`;
    }

    return {
      ruleId: rule.id,
      ruleName: rule.rule_name || rule.name,
      ruleType: rule.rule_type,
      isActive,
      threshold: windowDays,
      currentValue: daysUntilArrival,
      adjustment: { type: adjustmentType, value: adjustmentValue },
      adjustmentAmount,
      reason,
    };
  }

  /**
   * Per-room application of the exclusive channel_override gate on the
   * aggregated active-rules list (calendar badges). For each room type the
   * winner is resolved with the SAME shared selectPriceRules used by the
   * engine and the bridge; a rule entry survives only for room types where
   * it is not silenced. Entries without a roomTypes list (single-room view)
   * are treated as covering the requested room type.
   */
  private applyExclusiveGateToActiveRules(
    activeRules: ActiveRuleInfo['rules'],
    rules: PricingRule[],
    roomTypeCode?: string,
    allRoomTypeCodes: string[] = []
  ): ActiveRuleInfo['rules'] {
    const activeIds = new Set(activeRules.map((r) => r.id));
    const activeExclusives = rules.filter(
      (r) => isExclusiveChannelOverride(r) && activeIds.has(r.id)
    );
    if (activeExclusives.length === 0) return activeRules;

    const roomCodes = roomTypeCode
      ? [roomTypeCode]
      : allRoomTypeCodes.length > 0
        ? allRoomTypeCodes
        : [''];
    const winnerByRoom = new Map<string, string>();
    for (const code of roomCodes) {
      const covering = activeExclusives.filter(
        (r) => r.scope !== 'room_type' || (r.room_type_ids ?? []).includes(code)
      );
      if (covering.length > 0) {
        const winner = selectPriceRules(covering)[0];
        if (winner) winnerByRoom.set(code, winner.id);
      }
    }
    if (winnerByRoom.size === 0) return activeRules;

    return activeRules.flatMap((entry) => {
      // Exclusivity silences PRICE rules only — restriction rules survive.
      if (entry.ruleType === 'min_los') return [entry];
      const entryRooms = entry.roomTypes ?? roomCodes;
      const surviving = entryRooms.filter((code) => {
        const winnerId = winnerByRoom.get(code);
        return winnerId == null || winnerId === entry.id;
      });
      if (surviving.length === 0) return [];
      return [entry.roomTypes ? { ...entry, roomTypes: surviving } : entry];
    });
  }

  private createInactiveResult(
    rule: PricingRule,
    threshold: number,
    currentValue: number,
    reason: string
  ): RuleEvaluationResult {
    return {
      ruleId: rule.id,
      ruleName: rule.rule_name || rule.name,
      ruleType: rule.rule_type,
      isActive: false,
      threshold,
      currentValue,
      adjustment: { type: 'percentage', value: 0 },
      adjustmentAmount: 0,
      reason,
    };
  }

  /**
   * Get active rules count for each date in a range
   * Returns a map of date -> number of active rules
   * @param roomTypeCode - Optional room type code to filter rules by scope
   */
  async getActiveRulesCountForRange(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    roomTypeCode?: string
  ): Promise<Map<string, { count: number; totalAdjustmentPercent: number }>> {
    // Get occupancy data for the range
    const occupancyMap = await this.getOccupancyForRange(
      propertyId,
      startDate,
      endDate
    );

    // Get all active rules
    const { data: rules, error } = await supabase
      .from('pricing_rules')
      .select('*')
      .eq('property_id', propertyId)
      .eq('active', true)
      .order('priority');

    if (error || !rules) {
      console.error('[PricingRulesCalculator] Error fetching rules:', error);
      return new Map();
    }

    // Pre-fetch gap dates if any gap_filling rules exist
    const hasGapFillingRules = rules.some((r) => r.rule_type === 'gap_filling');
    const gapDurations = hasGapFillingRules
      ? await this.getGapDatesForRange(propertyId, startDate, endDate)
      : new Map<string, number[]>();

    const result = new Map<
      string,
      { count: number; totalAdjustmentPercent: number }
    >();

    // For each date, evaluate rules
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const occupancyStatus = occupancyMap.get(dateStr);
      const daysUntilArrival = differenceInDays(currentDate, new Date());

      const overallOccupancy = occupancyStatus?.overall || 0;

      // Use room-type-specific occupancy if roomTypeCode is provided, otherwise use overall
      let roomTypeOccupancy = overallOccupancy;
      if (roomTypeCode && occupancyStatus?.byRoomType) {
        const roomOccupancy = occupancyStatus.byRoomType.find(
          (rt) => rt.roomTypeCode === roomTypeCode
        );
        if (roomOccupancy) {
          roomTypeOccupancy = roomOccupancy.occupancy;
        }
      }

      // Gather all gap durations touching this date (room-type-scoped if
      // possible, otherwise property-wide). The rule decides whether any
      // duration satisfies its [gap_min, gap_max].
      const gapDurationsForDate = this.getGapDurationsForDate(
        gapDurations,
        dateStr,
        roomTypeCode
      );

      const activeRulesForDate: PricingRule[] = [];
      for (const rule of rules) {
        // Quick evaluation with room type scope check
        const isActive = this.quickEvaluateRule(
          rule as PricingRule,
          dateStr,
          roomTypeOccupancy,
          daysUntilArrival,
          gapDurationsForDate,
          roomTypeCode,
          overallOccupancy
        );
        if (isActive) activeRulesForDate.push(rule as PricingRule);
      }

      // Exclusive channel_override gate (shared with engine/bridge): only
      // price rules are silenced — min_los restriction rules stay counted.
      const participants = [
        ...selectPriceRules(
          activeRulesForDate.filter((r) => r.rule_type !== 'min_los')
        ),
        ...activeRulesForDate.filter((r) => r.rule_type === 'min_los'),
      ];
      const totalAdjustmentPercent = participants.reduce(
        (sum, r) => sum + ((r.actions?.price_adjustment?.value as number) || 0),
        0
      );

      result.set(dateStr, {
        count: participants.length,
        totalAdjustmentPercent,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return result;
  }

  /**
   * Get active rules with details for each date in a range
   * Returns a map of date -> { count, totalAdjustmentPercent, rules }
   *
   * For room-type-specific rules (when no roomTypeCode is passed):
   * - Evaluates the rule against each of its target room types
   * - Includes the rule if it's active for ANY of its target room types
   * - Adds roomTypes field to indicate which room types are affected
   */
  async getActiveRulesDetailsForRange(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    roomTypeCode?: string
  ): Promise<Map<string, ActiveRuleInfo>> {
    const occupancyMap = await this.getOccupancyForRange(
      propertyId,
      startDate,
      endDate
    );

    const { data: rules, error } = await supabase
      .from('pricing_rules')
      .select('*')
      .eq('property_id', propertyId)
      .eq('active', true)
      .order('priority');

    if (error || !rules) {
      console.error('[PricingRulesCalculator] Error fetching rules:', error);
      return new Map();
    }

    // Pre-fetch gap durations if any gap_filling rules exist
    const hasGapFillingRules = rules.some((r) => r.rule_type === 'gap_filling');
    const gapDurations = hasGapFillingRules
      ? await this.getGapDatesForRange(propertyId, startDate, endDate)
      : new Map<string, number[]>();

    // Debug: Log all rules and their scopes
    console.log(
      '[PricingRulesCalculator] Fetched rules:',
      rules.map((r) => ({
        id: r.id,
        name: r.rule_name || r.name,
        scope: r.scope,
        room_type_ids: r.room_type_ids,
        rule_type: r.rule_type,
        active: r.active,
      }))
    );

    const result = new Map<string, ActiveRuleInfo>();

    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      const occupancyStatus = occupancyMap.get(dateStr);
      const daysUntilArrival = differenceInDays(currentDate, new Date());
      const overallOccupancy = occupancyStatus?.overall || 0;

      const activeRules: ActiveRuleInfo['rules'] = [];

      for (const rule of rules) {
        const typedRule = rule as PricingRule;

        // Handle room-type-specific rules differently when no roomTypeCode is passed
        if (typedRule.scope === 'room_type' && !roomTypeCode) {
          const targetRoomTypes = typedRule.room_type_ids || [];
          const activeForRoomTypes: string[] = [];

          for (const targetRoomType of targetRoomTypes) {
            const roomOccupancy = occupancyStatus?.byRoomType?.find(
              (rt) => rt.roomTypeCode === targetRoomType
            );
            const rtOccupancy = roomOccupancy?.occupancy || 0;

            const isActiveForRoomType = this.quickEvaluateRule(
              typedRule,
              dateStr,
              rtOccupancy,
              daysUntilArrival,
              this.getGapDurationsForDate(
                gapDurations,
                dateStr,
                targetRoomType
              ),
              targetRoomType,
              overallOccupancy
            );

            if (isActiveForRoomType) {
              activeForRoomTypes.push(targetRoomType);
            }
          }

          if (activeForRoomTypes.length > 0) {
            const adjustmentValue =
              (rule.actions?.price_adjustment?.value as number) || 0;
            activeRules.push({
              id: rule.id,
              name: rule.rule_name || rule.name || 'Unnamed Rule',
              ruleType: rule.rule_type,
              adjustmentPercent: adjustmentValue,
              roomTypes: activeForRoomTypes,
            });
          }
        } else {
          // For global rules or when roomTypeCode is specified
          if (roomTypeCode) {
            let rtOccupancy = overallOccupancy;
            if (occupancyStatus?.byRoomType) {
              const roomOccupancy = occupancyStatus.byRoomType.find(
                (rt) => rt.roomTypeCode === roomTypeCode
              );
              if (roomOccupancy) {
                rtOccupancy = roomOccupancy.occupancy;
              }
            }

            const isActive = this.quickEvaluateRule(
              typedRule,
              dateStr,
              rtOccupancy,
              daysUntilArrival,
              this.getGapDurationsForDate(gapDurations, dateStr, roomTypeCode),
              roomTypeCode,
              overallOccupancy
            );

            if (isActive) {
              const adjustmentValue =
                (rule.actions?.price_adjustment?.value as number) || 0;
              activeRules.push({
                id: rule.id,
                name: rule.rule_name || rule.name || 'Unnamed Rule',
                ruleType: rule.rule_type,
                adjustmentPercent: adjustmentValue,
              });
            }
          } else {
            // Global rule without specific roomTypeCode - evaluate against EACH room type
            const allRoomTypes = occupancyStatus?.byRoomType || [];
            const activeForRoomTypes: string[] = [];

            for (const roomType of allRoomTypes) {
              const rtOccupancy = roomType.occupancy || 0;

              const isActiveForRoomType = this.quickEvaluateRule(
                typedRule,
                dateStr,
                rtOccupancy,
                daysUntilArrival,
                this.getGapDurationsForDate(
                  gapDurations,
                  dateStr,
                  roomType.roomTypeCode
                ),
                undefined,
                overallOccupancy
              );

              if (isActiveForRoomType) {
                activeForRoomTypes.push(roomType.roomTypeCode);
              }
            }

            if (activeForRoomTypes.length > 0) {
              const adjustmentValue =
                (rule.actions?.price_adjustment?.value as number) || 0;
              activeRules.push({
                id: rule.id,
                name: rule.rule_name || rule.name || 'Unnamed Rule',
                ruleType: rule.rule_type,
                adjustmentPercent: adjustmentValue,
                roomTypes: activeForRoomTypes,
              });
            }
          }
        }
      }

      // Exclusive channel_override gate — keep the badge consistent with the
      // engine/bridge: silenced rules must not be counted as active.
      const gatedRules = this.applyExclusiveGateToActiveRules(
        activeRules,
        rules as PricingRule[],
        roomTypeCode,
        occupancyStatus?.byRoomType?.map((rt) => rt.roomTypeCode) ?? []
      );
      result.set(dateStr, {
        count: gatedRules.length,
        totalAdjustmentPercent: gatedRules.reduce(
          (sum, r) => sum + r.adjustmentPercent,
          0
        ),
        rules: gatedRules,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return result;
  }

  /**
   * Fetch identified gap dates as a Map of "roomTypeCode-date" → durations[].
   *
   * Returns an ARRAY of durations per date key because multiple gap records can
   * cover the same date (e.g., a long 5-night gap and a shorter 2-night
   * subdivision both touching the same day). Rules with `gap_min`/`gap_max`
   * filters need access to every duration that touches the date to evaluate
   * whether ANY matches — without this, chip and price evaluation collapse to
   * "is there any gap at all", which silently drops the duration constraint.
   *
   * Each entry is verified via `verifyGapIsReal` so callers see the same
   * "real gaps" the chip UI sees — preventing chip ↔ price divergence.
   *
   * Made public so revenue-calculation pipelines can apply gap_filling rules
   * to the exact same dates as the displayed chips.
   */
  async getGapDatesForRange(
    propertyId: string,
    startDate: Date,
    endDate: Date
  ): Promise<Map<string, number[]>> {
    const gapDurations = new Map<string, number[]>();

    const { data: roomTypes } = await supabase
      .from('room_types')
      .select('id, webhotelier_code')
      .eq('property_id', propertyId);

    if (!roomTypes) return gapDurations;

    const idToCode = new Map<string, string>();
    for (const rt of roomTypes) {
      if (rt.webhotelier_code) idToCode.set(rt.id, rt.webhotelier_code);
    }

    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');

    const { data: gaps } = await supabase
      .from('reservation_gaps')
      .select('room_type_id, start_date, end_date')
      .eq('property_id', propertyId)
      .eq('status', 'identified')
      .gte('end_date', startStr)
      .lte('start_date', endStr);

    if (!gaps) return gapDurations;

    for (const gap of gaps) {
      const roomCode = idToCode.get(gap.room_type_id);
      if (!roomCode) continue;
      const gapStart = new Date(gap.start_date);
      const gapEnd = new Date(gap.end_date);

      // Skip phantom gaps: rows in reservation_gaps marked 'identified' that
      // no longer correspond to actual availability constraints. Without this
      // check, the UI flickers as the reconciler expires them ~1-2s after load.
      const isReal = await this.verifyGapIsReal(
        propertyId,
        roomCode,
        gapStart,
        gapEnd
      );
      if (!isReal) continue;

      const duration = differenceInDays(gapEnd, gapStart);
      for (let d = new Date(gapStart); d < gapEnd; d.setDate(d.getDate() + 1)) {
        const key = `${roomCode}-${format(d, 'yyyy-MM-dd')}`;
        const existing = gapDurations.get(key);
        if (existing) {
          existing.push(duration);
        } else {
          gapDurations.set(key, [duration]);
        }
      }
    }

    return gapDurations;
  }

  /**
   * Helper: get all gap durations touching a (roomCode, date) pair, or any
   * gap touching the date if roomCode is omitted (property-wide rules).
   */
  private getGapDurationsForDate(
    gapDurations: Map<string, number[]>,
    dateStr: string,
    roomTypeCode?: string
  ): number[] {
    if (roomTypeCode) {
      return gapDurations.get(`${roomTypeCode}-${dateStr}`) ?? [];
    }
    // Property-wide: any room type with a gap on this date contributes
    const all: number[] = [];
    for (const [key, durations] of gapDurations) {
      if (key.endsWith(`-${dateStr}`)) all.push(...durations);
    }
    return all;
  }

  /**
   * Verify that a gap reported by `reservation_gaps` corresponds to actual
   * availability constraints in `calendar_data`. Returns false for "phantom"
   * gaps left over from cancelled reservations that the reconciler hasn't
   * expired yet.
   *
   * Definition of a real gap (strict mode):
   *   • During the gap window, the room type has SOME availability
   *     (otherwise it's not a fillable gap — it's just fully booked).
   *   • BOTH adjacent boundary days (the day before start AND the end day)
   *     show booked_rooms >= total_rooms. Strict-both chosen to suppress
   *     phantom/one-sided gaps that produced misleading chips in the UI.
   *
   * The `calendar_data` table has columns:
   *   date | room_type_code | total_rooms | booked_rooms | available_rooms |
   *   occupancy_percentage
   *
   * TODO(user): implement. Suggested approach:
   *   1. Query calendar_data once for [gapStart - 1 day, gapEnd] filtered by
   *      property_id + room_type_code (single round-trip, not per-day).
   *   2. Walk the rows: confirm interior days have available_rooms > 0
   *      AND the boundary days (start-1 or end) show booked_rooms === total_rooms.
   *   3. Return true only if both pass.
   *
   * If you'd rather use a stricter or looser definition (e.g. boundary days
   * must be on BOTH sides, or "near-full" = >= 90% instead of 100%), encode
   * that here — it's a domain call.
   */
  private async verifyGapIsReal(
    propertyId: string,
    roomTypeCode: string,
    gapStart: Date,
    gapEnd: Date
  ): Promise<boolean> {
    // Convention: gapStart is inclusive, gapEnd is exclusive (matches caller loop).
    // We need: [gapStart - 1 day  ...  gapEnd] inclusive on both ends, because
    // the "before" boundary is the night before gapStart, and the "after"
    // boundary is the gapEnd day itself (= the next booking's check-in).
    const dayBeforeStr = format(subDays(gapStart, 1), 'yyyy-MM-dd');
    const dayAfterStr = format(gapEnd, 'yyyy-MM-dd');

    const { data: rows, error } = await supabase
      .from('calendar_data')
      .select('date, total_rooms, booked_rooms, available_rooms')
      .eq('property_id', propertyId)
      .eq('room_type_code', roomTypeCode)
      .gte('date', dayBeforeStr)
      .lte('date', dayAfterStr);

    // Defensive: if we can't verify (error, no rows), don't show the chip.
    // It's better to under-report than to flicker.
    if (error || !rows || rows.length === 0) {
      console.log('[verifyGapIsReal] REJECTED (no data):', {
        roomTypeCode,
        gap: `${format(gapStart, 'yyyy-MM-dd')} → ${format(gapEnd, 'yyyy-MM-dd')}`,
        error: error?.message,
        rowCount: rows?.length ?? 0,
      });
      return false;
    }

    const byDate = new Map<
      string,
      { total: number; booked: number; available: number }
    >();
    for (const r of rows) {
      byDate.set(r.date, {
        total: Number(r.total_rooms) || 0,
        booked: Number(r.booked_rooms) || 0,
        available: Number(r.available_rooms) || 0,
      });
    }

    // Condition 1: interior days [gapStart, gapEnd) must have availability,
    // otherwise it's a sold-out period, not a fillable gap.
    for (
      let d = new Date(gapStart);
      d < gapEnd;
      d = addDays(d, 1) // immutable; avoids the date-mutation bug pattern
    ) {
      const dateStr = format(d, 'yyyy-MM-dd');
      const row = byDate.get(dateStr);
      if (!row || row.available <= 0) {
        console.log('[verifyGapIsReal] REJECTED (interior sold-out):', {
          roomTypeCode,
          dateStr,
          row,
        });
        return false;
      }
    }

    // Condition 2: BOTH adjacent boundary days must be fully booked — the gap
    // is truly "enclosed" by reservations on both sides. Strict mode chosen to
    // avoid false-positive chips from phantom or one-sided gaps. Trade-off:
    // gaps at the trailing/leading edge of a booking cluster won't trigger.
    const isFull = (row: { booked: number; total: number } | undefined) =>
      !!row && row.total > 0 && row.booked >= row.total;

    const beforeRow = byDate.get(dayBeforeStr);
    const afterRow = byDate.get(dayAfterStr);
    const beforeFull = isFull(beforeRow);
    const afterFull = isFull(afterRow);
    const passed = beforeFull && afterFull;

    console.log(
      `[verifyGapIsReal] ${passed ? 'ACCEPTED' : 'REJECTED (no full boundary)'}:`,
      {
        roomTypeCode,
        gap: `${format(gapStart, 'yyyy-MM-dd')} → ${format(gapEnd, 'yyyy-MM-dd')}`,
        dayBefore: { date: dayBeforeStr, row: beforeRow, isFull: beforeFull },
        dayAfter: { date: dayAfterStr, row: afterRow, isFull: afterFull },
      }
    );

    return passed;
  }

  /**
   * Quick rule evaluation for counting (without detailed breakdown)
   * Now includes optional roomTypeCode parameter for room-type-specific rules
   */
  private resolveOccupancy(
    rule: PricingRule,
    roomTypeOccupancy: number,
    overallOccupancy: number
  ): number {
    const scope =
      (rule.conditions?.['occupancy_scope'] as OccupancyScope) ?? 'property';
    return scope === 'room_type' ? roomTypeOccupancy : overallOccupancy;
  }

  private getOccupancyScopeLabel(rule: PricingRule): string {
    const scope =
      (rule.conditions?.['occupancy_scope'] as OccupancyScope) ?? 'property';
    return scope === 'room_type' ? 'Room type' : 'Property';
  }

  private quickEvaluateRule(
    rule: PricingRule,
    dateStr: string,
    roomTypeOccupancy: number,
    daysUntilArrival: number,
    gapDurationsForDate: number[] = [],
    roomTypeCode?: string,
    overallOccupancy?: number
  ): boolean {
    // Check date range (top-level fields or conditions.date_range)
    const condDR = rule.conditions?.['date_range'] as
      | { start_date?: string; end_date?: string; start?: string; end?: string }
      | undefined;
    const vFrom = rule.valid_from || condDR?.start_date || condDR?.start;
    const vUntil = rule.valid_until || condDR?.end_date || condDR?.end;
    if (vFrom && dateStr < vFrom) return false;
    if (vUntil && dateStr > vUntil) return false;

    // Check room type scope - if rule is room_type-specific, verify room type is in list
    if (rule.scope === 'room_type') {
      if (!roomTypeCode) {
        // No room type specified but rule is room-type-specific - skip it
        return false;
      }
      if (!rule.room_type_ids || !rule.room_type_ids.includes(roomTypeCode)) {
        return false;
      }
    }
    // If scope is 'global' or undefined, it applies to all room types

    const occupancy = this.resolveOccupancy(
      rule,
      roomTypeOccupancy,
      overallOccupancy ?? roomTypeOccupancy
    );

    // Optional lead-time gate for occupancy rules — mirrors the edge function.
    const withinOccupancyWindow = isWithinBookingWindow(
      daysUntilArrival,
      resolveBookingWindowBounds(rule)
    );

    switch (rule.rule_type) {
      case 'high_occupancy': {
        // Defaults mirror the pricing-engine edge function (gte / 0).
        const threshold =
          (rule.conditions?.['occupancy_threshold'] as number) ?? 0;
        const operator =
          (rule.conditions?.['operator'] as ComparisonOperator) ?? 'gte';
        return (
          withinOccupancyWindow &&
          evaluateCondition(occupancy, operator, threshold)
        );
      }
      case 'low_occupancy': {
        // Defaults mirror the pricing-engine edge function ('lt' / 100).
        const threshold =
          (rule.conditions?.['occupancy_threshold'] as number) ?? 100;
        const operator =
          (rule.conditions?.['operator'] as ComparisonOperator) ?? 'lt';
        return (
          withinOccupancyWindow &&
          evaluateCondition(occupancy, operator, threshold)
        );
      }
      case 'booking_window': {
        const occupancyThreshold =
          (rule.conditions?.['occupancy_threshold'] as number) ?? 60;
        const occupancyOperator =
          (rule.conditions?.['occupancy_operator'] as ComparisonOperator) ??
          'gte';
        return (
          isWithinBookingWindow(
            daysUntilArrival,
            resolveBookingWindowBounds(rule, 14)
          ) &&
          evaluateCondition(occupancy, occupancyOperator, occupancyThreshold)
        );
      }
      case 'min_los': {
        // MinLOS rules with a seasonal date range only apply within that range
        const dateRange = rule.conditions?.['date_range'] as
          | {
              start_date?: string;
              end_date?: string;
              start?: string;
              end?: string;
            }
          | undefined;
        if (dateRange) {
          const rangeStart = dateRange.start_date || dateRange.start;
          const rangeEnd = dateRange.end_date || dateRange.end;
          if (rangeStart && dateStr < rangeStart) return false;
          if (rangeEnd && dateStr > rangeEnd) return false;
        }
        // Day-of-week gate (0=Sunday..6=Saturday), same as the engine. Parse
        // Y-M-D locally so the calendar weekday doesn't shift with timezones.
        const dayOfWeek = rule.conditions?.['day_of_week'] as
          | number[]
          | undefined;
        if (dayOfWeek && dayOfWeek.length > 0) {
          const [y = 0, m = 1, d = 1] = dateStr.split('-').map(Number);
          if (!dayOfWeek.includes(new Date(y, m - 1, d).getDay())) {
            return false;
          }
        }
        return true;
      }
      case 'gap_filling': {
        // Gap filling rules apply only when the date is in an actual verified
        // gap AND that gap's duration matches the rule's [gap_min, gap_max]
        // window. Without the duration check, the chip would show "active"
        // even when the bridge later rejects the rule for duration mismatch —
        // exactly the chip ↔ price divergence we're fixing.
        if (gapDurationsForDate.length === 0) return false;
        // Occupancy gate — the engine BLOCKS the rule once occupancy reaches
        // the threshold; the chip must not show it as active then.
        const occupancyThreshold = rule.conditions?.['occupancy_threshold'] as
          | number
          | undefined;
        if (occupancyThreshold != null && occupancy >= occupancyThreshold) {
          return false;
        }
        const gapMin = (rule.conditions?.['gap_min'] as number) ?? 1;
        const gapMax = (rule.conditions?.['gap_max'] as number) ?? Infinity;
        return gapDurationsForDate.some((d) => d >= gapMin && d <= gapMax);
      }
      case 'channel_override':
        // Unconditional for its period — scope and date range were already
        // checked above. Exclusive silencing of OTHER rules happens in the
        // per-date aggregation, not here.
        return true;
      default:
        return false;
    }
  }

  /**
   * Get how many days in the next N days each rule would be active
   * For room-type-specific rules, evaluates against the first applicable room type
   */
  async getRuleActiveDaysCount(
    propertyId: string,
    daysAhead: number = 30
  ): Promise<Map<string, RuleActiveDaysCount>> {
    const today = new Date();
    const endDate = new Date();
    endDate.setDate(today.getDate() + daysAhead);

    // Get occupancy for range
    const occupancyMap = await this.getOccupancyForRange(
      propertyId,
      today,
      endDate
    );

    // Get all rules (including inactive for status display)
    const { data: rules, error } = await supabase
      .from('pricing_rules')
      .select('*')
      .eq('property_id', propertyId)
      .order('priority');

    if (error || !rules) {
      return new Map();
    }

    // Pre-fetch gap durations if any gap_filling rules exist
    const hasGapFillingRules = rules.some((r) => r.rule_type === 'gap_filling');
    const gapDurations = hasGapFillingRules
      ? await this.getGapDatesForRange(propertyId, today, endDate)
      : new Map<string, number[]>();

    const result = new Map<string, RuleActiveDaysCount>();

    for (const rule of rules) {
      if (!rule.active) {
        result.set(rule.id, {
          ruleId: rule.id,
          activeDays: 0,
          totalDays: daysAhead,
          nextActiveDate: null,
        });
        continue;
      }

      let activeDays = 0;
      let nextActiveDate: string | null = null;
      const currentDate = new Date(today);

      // For room-type-specific rules, use the first room type in the list
      const roomTypeCode =
        rule.scope === 'room_type' && rule.room_type_ids?.length
          ? rule.room_type_ids[0]
          : undefined;

      while (currentDate <= endDate) {
        const dateStr = format(currentDate, 'yyyy-MM-dd');
        const occupancyStatus = occupancyMap.get(dateStr);
        const daysUntilArrival = differenceInDays(currentDate, today);

        const overallOccupancy = occupancyStatus?.overall || 0;
        // Use room-type-specific occupancy for room-type rules
        let rtOccupancy = overallOccupancy;
        if (roomTypeCode && occupancyStatus?.byRoomType) {
          const roomOccupancy = occupancyStatus.byRoomType.find(
            (rt) => rt.roomTypeCode === roomTypeCode
          );
          if (roomOccupancy) {
            rtOccupancy = roomOccupancy.occupancy;
          }
        }

        const gapDurationsForDate = this.getGapDurationsForDate(
          gapDurations,
          dateStr,
          roomTypeCode
        );

        const isActive = this.quickEvaluateRule(
          rule as PricingRule,
          dateStr,
          rtOccupancy,
          daysUntilArrival,
          gapDurationsForDate,
          roomTypeCode,
          overallOccupancy
        );

        if (isActive) {
          activeDays++;
          if (!nextActiveDate) {
            nextActiveDate = dateStr;
          }
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      result.set(rule.id, {
        ruleId: rule.id,
        activeDays,
        totalDays: daysAhead,
        nextActiveDate,
      });
    }

    return result;
  }

  /**
   * Get today's occupancy status for displaying in rules panel
   */
  async getTodayOccupancy(propertyId: string): Promise<OccupancyStatus> {
    return this.getOccupancyForDate(propertyId, new Date());
  }
}

// Export singleton
export const pricingRulesCalculatorService =
  new PricingRulesCalculatorService();
